import samlConfigurationStyle from "./saml-configuration.module.css";

import { useState, useContext, useCallback, useEffect } from "react";
import { UNSAFE_NavigationContext as NavigationContext, useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Hook
import useMessage from "hooks/useMessage";
import usePrompt, { ConfirmMessageModal1Id } from "hooks/useBlocker";

// Slice
import { uiActions } from "stores/slice/ui";
import { selectOrgLevel } from "stores/slice/pageContext";

// Component
import Hr from "components/Hr";
import Breadcrumbs from "components/Breadcrumb";
import RadioButton from "components/RadioButton";
import InlineTitle from "components/InlineTitle";
import MessageBoxGroup from "components/MessageBoxGroup";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import TrustedIdentityProvider from "./components/TrustedIdentityProvider";
import SamlRoles from "./components/SamlRoles";

// Controller
import controller from "./controller";

// Const
import { reloadState } from "const/nuclias/system";
import { organizationType } from "const/nuclias/organization";
import { pathList } from "./const";

const SamlConfiguration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Slice
  const isOrgLevel = useSelector(selectOrgLevel) === organizationType.org ? true : false;
  const pageContext = useSelector(state => state.pageContext);
  const { reloadStatus } = useSelector(state => state.fetchStatus);

  // Context
  const navigatorContext = useContext(NavigationContext);

  // State
  const [isChanginOrg, setIsChanginOrg] = useState(false);
  const [webServer, setWebServer] = useState('');
  const [samlSsoState, setSamlSsoState] = useState(false);
  const [originMetaDataSetList, setOrigindMetaDataSetList] = useState([]);
  const [originSamlRoleList, setOrigindSamlRoleList] = useState([]);
  const [stopTransition, setStopTransition] = useState(false);

  // Hook
  const {
    messages,
    resetMessages,
    setErrorMessage,
    setWarningMessage
  } = useMessage();

  // Variable
  const selectedOrg = controller.getSelectedOrg(isOrgLevel, pageContext);
  let samlEentityId = selectedOrg ? `${selectedOrg.id}.sp.saml.nuclias.com` : '';
  let samlUrl = webServer ? `${webServer}oauth/${selectedOrg.id}/samlLogin` : '';
  usePrompt(stopTransition, samlSsoState && originMetaDataSetList.length === 0);

  // Method
  const init = useCallback(async (fullRefreh = true) => {
    resetMessages();

    try {
      dispatch(uiActions.showSpinner());

      const authResourcesResponse = await controller.getAuthResources();
      if (authResourcesResponse.error) {
        throw authResourcesResponse.error;
      }
      setWebServer(authResourcesResponse.params.webserver);

      const metadataSetResponse = await controller.getMetaDataSet(selectedOrg.id);
      if (metadataSetResponse.error) {
        throw metadataSetResponse.error;
      }

      const metadataSetList = controller.parseRawData('MetadataSet', metadataSetResponse.data);
      setOrigindMetaDataSetList(metadataSetList);

      if (fullRefreh) {
        if (metadataSetList.length === 0) {
          setSamlSsoState(false);
        } else {
          setSamlSsoState(controller.checkSamlSsoState(metadataSetList));
        }
      }

      const SamlRoleResponse = await controller.getSamlRole(selectedOrg.id);
      if (SamlRoleResponse.error) {
        throw SamlRoleResponse.error;
      }

      const samlRoleList = controller.parseRawData(
        'SamlRole',
        SamlRoleResponse.data,
        pageContext.orgTreeMap,
        selectedOrg
      );
      setOrigindSamlRoleList(samlRoleList);

      dispatch(uiActions.hideSpinner());
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideSpinner());
    }
  }, [dispatch, pageContext.orgTreeMap, selectedOrg]);

  const changesamlSsoState = useCallback(async () => {
    resetMessages();

    if (originMetaDataSetList.length === 0) {
      setSamlSsoState(!samlSsoState);

      if (!samlSsoState) {
        setWarningMessage('6c9e5eb3fa')
      }

      return;
    }

    resetMessages();

    try {
      dispatch(uiActions.showSpinner());
      const response = await controller.changeSamlSsoState(selectedOrg.id, originMetaDataSetList);

      if (response.error) {
        throw response.error;
      }

      dispatch(uiActions.hideSpinner());
      init();
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideSpinner());
    }
  }, [dispatch, init, originMetaDataSetList, samlSsoState, selectedOrg.id]);

  const confirmLeving = (answer) => {
    if (answer) {
      setStopTransition(true);
    } else {
      setStopTransition(false);
    }

    document.getElementById(ConfirmMessageModal1Id).hidden = true;
  }

  // Side effect
  useEffect(() => {
    if (reloadStatus.data?.reloadState === reloadState.doing) {
      setIsChanginOrg(true);
    } else {
      setIsChanginOrg(false);
    }
  }, [reloadStatus]);

  useEffect(() => {
    init();
  }, [init, pageContext]);

  useEffect(() => {
    if (stopTransition) {
      navigate(navigatorContext.navigator.lastPath.pathname)
    }
  }, [navigate, navigatorContext.navigator.lastPath?.pathname, stopTransition]);

  return (
    <>
      <Breadcrumbs pathList={pathList} />

      <div className="layout-container layout-container--column layout-container--fluid">
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType='es'
          WarningMessagesType='i18n'
          onClose={resetMessages}
        />

        {!isChanginOrg && (
          <>
            <div>
              <InlineTitle label="SAML CONFIGURATION" isNonUnderline />
              <Row className={`${samlConfigurationStyle['enable-btn-group']} mt-3`}>
                <Col sm={2} md={2} xl={1} className="form-title">
                  SAML SSO
                </Col>
                <Col sm={10} md={10} xl={11} style={{ display: 'flex' }}>
                  <div className="me-3">
                    <RadioButton
                      id="Enable"
                      name="Enable"
                      label="2faec1f9f8"
                      checked={samlSsoState}
                      onChange={changesamlSsoState}
                    />
                  </div>
                  <div>
                    <RadioButton
                      id="Disable"
                      name="Disable"
                      label="bcfaccebf7"
                      checked={!samlSsoState}
                      onChange={changesamlSsoState}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <div className={`${!samlSsoState ? samlConfigurationStyle['mask'] : ''}`}>
              <div className="mb-5">
                <Row className={`${samlConfigurationStyle['enable-btn-group']} mt-3`}>
                  <Col sm={2} md={2} xl={1} className="form-title">
                    {t('5536ec7a11')}
                  </Col>
                  <Col sm={10} md={10} xl={11}>
                    {samlEentityId}
                  </Col>
                </Row>
                <Row className={`${samlConfigurationStyle['enable-btn-group']} mt-3`}>
                  <Col sm={2} md={2} xl={1} className="form-title">
                    {t('ad882e4bf6')}
                  </Col>
                  <Col sm={10} md={10} xl={11}>
                    {samlUrl}
                  </Col>
                </Row>
              </div>

              <Hr className="mb-4" />

              {/* TRUSTED IDENTITY PROVIDER ( IDP ) */}
              <TrustedIdentityProvider
                selectedOrg={selectedOrg}
                originMetaDataSetList={originMetaDataSetList}
                successCallback={() => init(false)}
              />

              <Hr className="mb-4" />

              {/* SAML ROLES */}
              <SamlRoles
                selectedOrg={selectedOrg}
                originSamlRoleList={originSamlRoleList}
                successCallback={() => init(false)}
              />
            </div>
            {/* Mask End */}

            <ConfirmMessageModal
              firstMessage='5ac59b084a'
              secondMessage=''
              leftButtonTitle='18ad756356'
              rightButtonTitle='014f452bbc'
              confirmLeving={confirmLeving}
            />
          </>
        )
        }
      </div>
    </>
  );
};

export default SamlConfiguration;
